import {
  ADS_WEB_GEO_ENABLED_COUNTRY,
  ADS_WEB_PLACEMENT_HOMEPAGE_ENABLED,
  ADS_WEB_PLACEMENT_USER_MENU_ENABLED,
} from '../../constants/optimizelyExperiments';
import ucmCountryCodes from '../../utils/loadScriptSupplier/ucmCountryCodeMap';

export const GAM_NETWORK_CODE = '3413884';
export const AD_LOAD_TIMEOUT_MS = 5000;

const IS_PROD = typeof ENV !== 'undefined' && ENV === 'prod';
const AD_PREFIX = IS_PROD ? '' : 'Test_';

const AD_CONTEXT_MAP = {
  home_page: 'home page',
  account_page: 'profile page',
  account_page_bottom: 'profile page',
};

const getAdContext = (page) => AD_CONTEXT_MAP[page] ?? page;

const AD_DIMENSIONS = {
  home_page: (viewportWidth) => (viewportWidth >= 768
    ? { width: 728, height: 90 }
    : { width: 300, height: 250 }),
  account_page: () => ({ width: 300, height: 250 }),
  account_page_bottom: () => ({ width: 300, height: 250 }),
};

const AD_PLACEMENTS = {
  home_page: `${AD_PREFIX}ResyWeb_Homepage_Mid_1`,
  account_page: `${AD_PREFIX}ResyWeb_Account_Rail_1`,
  account_page_bottom: `${AD_PREFIX}ResyWeb_Account_Rail_1`,
};

const REQUEST_PAGE_MAP = {
  home_page: 'ResyWeb_Homepage',
  account_page: 'ResyWeb_Account',
  account_page_bottom: 'ResyWeb_Account',
};

const getRequestPage = (page) => REQUEST_PAGE_MAP[page] ?? page;

// GPT size mappings: define all possible sizes per placement so GPT
// picks the correct one based on viewport without needing to destroy/recreate slots.
const AD_ALL_SIZES = {
  home_page: [[728, 90], [300, 250]],
  account_page: [[300, 250]],
  account_page_bottom: [[300, 250]],
};

const AD_SIZE_MAPPING = {
  home_page: [
    { viewport: [768, 0], sizes: [[728, 90]] },
    { viewport: [0, 0], sizes: [[300, 250]] },
  ],
};
const DEFAULT_LAZY_LOAD_OPTIONS = {
  enableLazyLoad: true,
  fetchMarginPercent: 50,
  renderMarginPercent: 20,
  mobileScaling: 2,
};

const getAdDimension = (page, viewportWidth = window.innerWidth) => {
  const dimensionFn = AD_DIMENSIONS[page];
  return dimensionFn ? dimensionFn(viewportWidth) : { width: 300, height: 250 };
};

const getAdPlacementId = (page) => AD_PLACEMENTS[page] ?? AD_PLACEMENTS.account_page;

/**
 * Returns lazy-load options derived from geo feature flag variables.
 * @param {object} experiments - experiments state from Redux store
 * @returns {{ enableLazyLoad: boolean, fetchMarginPercent: number, renderMarginPercent: number, mobileScaling: number }}
 */
const getGamLazyLoadOptions = (experiments) => {
  const lazyConfig = experiments?.[ADS_WEB_GEO_ENABLED_COUNTRY]?.variables?.json_variable || {};
  return {
    enableLazyLoad: !!lazyConfig.enable_lazy_loading,
    fetchMarginPercent: typeof lazyConfig.fetchMarginPercent === 'number'
      ? lazyConfig.fetchMarginPercent
      : DEFAULT_LAZY_LOAD_OPTIONS.fetchMarginPercent,
    renderMarginPercent: typeof lazyConfig.renderMarginPercent === 'number'
      ? lazyConfig.renderMarginPercent
      : DEFAULT_LAZY_LOAD_OPTIONS.renderMarginPercent,
    mobileScaling: typeof lazyConfig.mobileScaling === 'number'
      ? lazyConfig.mobileScaling
      : DEFAULT_LAZY_LOAD_OPTIONS.mobileScaling,
  };
};

const findSlot = (service, placementId, networkCode) => {
  const slotName = `/${networkCode}/${placementId}`;
  if (!service?.pubads) return undefined;
  return service.pubads().getSlots().find((slot) => slot.getAdUnitPath() === slotName);
};

const isAdRegistered = (service, placementId, networkCode) => !!findSlot(service, placementId, networkCode);

/**
 * Refresh an ad slot
 * @param {string} placementId
 * @param {string} networkCode
 */
const refreshAd = (placementId, networkCode) => {
  const { googletag } = window;
  googletag.cmd.push(() => {
    const slot = findSlot(googletag, placementId, networkCode);
    if (slot) {
      googletag.pubads().clear([slot]);
      googletag.pubads().refresh([slot]);
    }
  });
};

/**
 * Sets the targeting values for personalized marketing
 * @param {object} service - googletag service
 * @param {{ [key: string]: string | string[] }} payload
 */
const setTargeting = (service, payload) => {
  const pubads = service?.pubads();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null) pubads.setTargeting(key, value);
  });
};

/**
 * Defines a new ad slot for gpt
 * @param {Object} input
 * @param {GoogleTag} input.service
 * @param {string} input.placementId
 * @param {[[number, number]]} input.sizes
 * @param {{ viewport: [number, number], sizes: [number, number][] }[]} input.sizeMapping
 * @param {string} input.divId
 */
export const getDefineSlot = ({
  service,
  placementId,
  sizes,
  sizeMapping,
  divId,
}) => () => {
  const slot = service
    .defineSlot(`/${GAM_NETWORK_CODE}/${placementId}`, sizes, divId)
    .addService(service.pubads());

  if (sizeMapping) {
    let builder = service.sizeMapping();
    sizeMapping.forEach(({ viewport, sizes: mappedSizes }) => {
      builder = builder.addSize(viewport, mappedSizes);
    });
    slot.defineSizeMapping(builder.build());
  }
};

/**
 * Fully configure GAM
 * @param {object} obj - input object
 * @param {string} obj.placementId - placement ID
 * @param {string} obj.page - page identifier
 * @param {string} obj.divId - ID of the placement div
 * @param {object} obj.payload - targeting key/value pairs from one data function
 * @param {'empty' | 'segment_info_returned' | 'odf_call_error'} obj.odfResponse - ODF response status for analytics
 * @param {object} obj.gamOptions - GAM options such as lazy loading
 * @param {Function} obj.onAdEvent - callback for ad events (slotRenderEnded)
 * @returns {Function} cleanup function
 */
const configureGam = ({
  placementId,
  page,
  divId,
  payload,
  odfResponse,
  gamOptions = {},
  onAdEvent,
  loadTimeoutMs = AD_LOAD_TIMEOUT_MS,
}) => {
  if (!window.googletag) window.googletag = { cmd: [] };
  const { googletag } = window;
  onAdEvent?.({ type: 'gam_call', placementId: divId, odfResponse });

  let loadResolved = false;
  let loadTimeoutId = null;

  const clearLoadTimeout = () => {
    if (loadTimeoutId) {
      clearTimeout(loadTimeoutId);
      loadTimeoutId = null;
    }
  };

  const resolveLoad = (type, extra = {}) => {
    if (loadResolved) return;
    loadResolved = true;
    clearLoadTimeout();
    onAdEvent?.({ type, placementId: divId, ...extra });
  };

  loadTimeoutId = setTimeout(() => {
    resolveLoad('load_timeout');
  }, loadTimeoutMs);

  // Track the rendered creative so click events can be attributed to it.
  let lastAdId = null;

  const renderHandler = (event) => {
    if (!event.slot || event.slot.getSlotElementId() !== divId) return;
    if (event.isEmpty) {
      // Empty response: GAM responded in time so cancel the timeout, but
      // don't fire `ad_load = successful` since no creative loaded.
      loadResolved = true;
      clearLoadTimeout();
      onAdEvent?.({ type: 'empty', placementId: divId });
      return;
    }
    lastAdId = event.creativeId;
    onAdEvent?.({ type: 'rendered', placementId: divId });
    resolveLoad('load_success', { adId: event.creativeId });
  };

  const impressionHandler = (event) => {
    if (!event.slot || event.slot.getSlotElementId() !== divId) return;
    onAdEvent?.({
      type: 'impression',
      placementId: divId,
      adId: event.creativeId,
    });
  };

  // GPT serves creatives in cross-origin iframes and exposes no click event.
  // When the user clicks the creative, the ad iframe takes focus and the
  // parent window blurs, so detect a click when the focused element is an
  // iframe inside this slot's div.
  const handleWindowBlur = () => {
    const active = document.activeElement;
    if (!active || active.tagName !== 'IFRAME') return;
    const adRoot = document.getElementById(divId);
    if (!adRoot || !adRoot.contains(active)) return;
    onAdEvent?.({ type: 'click', placementId: divId, adId: lastAdId });
  };

  window.addEventListener('blur', handleWindowBlur);

  googletag.cmd.push(() => {
    googletag.pubads().addEventListener('slotRenderEnded', renderHandler);
    googletag.pubads().addEventListener('impressionViewable', impressionHandler);
  });

  if (isAdRegistered(googletag, placementId, GAM_NETWORK_CODE)) {
    // Slot already exists. If DOM is empty (route change or key-based
    // remount), refresh — GPT serves the correct size via sizeMapping.
    googletag.cmd.push(() => {
      const el = document.getElementById(divId);
      if (el && el.children.length === 0) {
        const existingSlot = findSlot(googletag, placementId, GAM_NETWORK_CODE);
        googletag.pubads().refresh([existingSlot]);
      }
    });
  } else {
    if (!window.googletagConfigured) {
      // Configure global GAM settings
      googletag.cmd.push(() => {
        googletag.pubads().enableSingleRequest();
        if (gamOptions?.enableLazyLoad) {
          googletag.pubads().enableLazyLoad({
            fetchMarginPercent: gamOptions.fetchMarginPercent,
            renderMarginPercent: gamOptions.renderMarginPercent,
            mobileScaling: gamOptions.mobileScaling,
          });
        }
        if (payload) {
          setTargeting(googletag, payload);
        }
        googletag.enableServices();
      });
      window.googletagConfigured = true;
    }

    // Define slot
    googletag.cmd.push(
      getDefineSlot({
        service: googletag,
        placementId,
        sizes: AD_ALL_SIZES[page] || [[300, 250]],
        sizeMapping: AD_SIZE_MAPPING[page],
        divId,
      })
    );

    if (!window.displayedSlots) {
      window.displayedSlots = new Set();
    }

    if (!window.displayedSlots.has(divId)) {
      window.displayedSlots.add(divId);
      googletag.cmd.push(() => {
        googletag.display(divId);
        // With SRA, display() alone won't request ads for slots added after
        // the initial page load. Refresh to trigger the ad request.
        if (window.googletagInitialLoadDone) {
          const slot = findSlot(googletag, placementId, GAM_NETWORK_CODE);
          if (slot) {
            googletag.pubads().refresh([slot]);
          }
        } else {
          window.googletagInitialLoadDone = true;
        }
      });
    }
  }

  return () => {
    clearLoadTimeout();
    window.removeEventListener('blur', handleWindowBlur);
    // Release synchronously so a remount can re-queue display(divId).
    window.displayedSlots?.delete(divId);
    // Defer destroy through cmd so pending commands survive when cleanup
    // runs before GPT has initialized.
    googletag.cmd.push(() => {
      if (googletag.destroySlots) {
        const slot = findSlot(googletag, placementId, GAM_NETWORK_CODE);
        if (slot) {
          googletag.destroySlots([slot]);
        }
      }
      googletag.pubads?.()?.removeEventListener?.('slotRenderEnded', renderHandler);
      googletag.pubads?.()?.removeEventListener?.('impressionViewable', impressionHandler);
    });
  };
};

const AD_PLACEMENT_FLAGS = {
  home_page: ADS_WEB_PLACEMENT_HOMEPAGE_ENABLED,
  account_page: ADS_WEB_PLACEMENT_USER_MENU_ENABLED,
  account_page_bottom: ADS_WEB_PLACEMENT_USER_MENU_ENABLED,
};

/**
 * Determines whether an ad should be displayed for a given page and country.
 * @param {object} experiments - experiments state from Redux store
 * @param {string} page - page identifier (e.g. 'home_page', 'account_page')
 * @param {string} countryCode - geo country code (e.g. 'US')
 * @returns {boolean}
 */
const shouldDisplayAd = (experiments, page, countryCode) => {
  const geoFlag = experiments?.[ADS_WEB_GEO_ENABLED_COUNTRY];
  const mappedLocale = countryCode ? ucmCountryCodes[countryCode.toUpperCase()] : undefined;
  const normalizedLocale = mappedLocale?.toLowerCase();
  const enabledCountries = geoFlag?.variables?.json_variable?.enabled_countries;
  const normalizedEnabledCountries = Array.isArray(enabledCountries)
    ? enabledCountries.map((countryLocale) => countryLocale?.toLowerCase())
    : [];
  const isGeoEnabled = geoFlag?.enabled
    && normalizedEnabledCountries.includes(normalizedLocale);

  const placementFlagKey = AD_PLACEMENT_FLAGS[page];
  const isPlacementEnabled = placementFlagKey
    ? !!experiments?.[placementFlagKey]?.enabled
    : false;

  return !!isGeoEnabled && isPlacementEnabled;
};

export {
  getAdContext, getAdDimension, getAdPlacementId, getRequestPage,
  configureGam, getGamLazyLoadOptions, refreshAd, setTargeting, shouldDisplayAd,
};
